import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs/Subscription';

import {
  StandardDiscussionFilesDto,
  StandardDiscussionMessageDto,
  StandardDiscussionMessagesDto,
  StandardDiscussionStateDto
} from '../../models/standard-discussion.model';
import { StandardDiscussionApiService } from '../../services/standard-discussion-api.service';
import { ViewDocxComponent } from '../../../../views/view-docx/view-docx.component';
import { AdminService } from '../../../../../services/admin.service';
import { ViewDocxService } from '../../../../../services/view-docx.service';
import { UserService } from '../../../../../services/user.service';

type StandardDiscussionViewState = 'loading' | 'ready' | 'access-denied' | 'load-error' | 'message-error';
type StandardDiscussionStoredFileType = 'RU_DOC' | 'RU_PPT' | 'RU_PDF' | 'EN_DOC' | 'EN_PPT' | 'EN_PDF';
type StandardDiscussionFileAction = 'doc' | 'pdf' | 'download';

interface StandardDiscussionPrimaryFile {
  type: StandardDiscussionStoredFileType;
  fileId: string | null;
  fileLink: string | null;
  action: StandardDiscussionFileAction;
  displayName: string;
}

@Component({
  selector: 'app-standard-discussion-page',
  templateUrl: './standard-discussion-page.component.html',
  styleUrls: ['./standard-discussion-page.component.css']
})
export class StandardDiscussionPageComponent implements OnInit, OnDestroy {
  public viewState: StandardDiscussionViewState = 'loading';
  public viewModel: StandardDiscussionStateDto;
  public messages: StandardDiscussionMessageDto[] = [];
  public messageText = '';
  public isMessagePosting = false;
  public isFileMutationInProgress = false;
  public fileMutationError = false;
  public fileMutationSuccess = false;
  public selectedFileName = '';
  public lastReplacedFileName = '';
  public storedPrimaryFileName = '';

  private activeStandardId = '';
  private subscriptions: Subscription[] = [];
  private nowProvider = () => new Date();

  constructor(private route: ActivatedRoute,
              private discussionApi: StandardDiscussionApiService,
              private modalService: NgbModal,
              private viewDocxService: ViewDocxService,
              private userService: UserService,
              private adminService: AdminService,
              private router: Router) {
  }

  get canSendMessage(): boolean {
    var text = this.trimmedMessage();
    return !!this.viewModel && this.viewModel.canWriteMessage
      && this.viewModel.discussionStatus === 'IN_DISCUSSION'
      && !this.isMessagePosting
      && text.length > 0 && text.length <= 2000;
  }

  get publicationFailureMessage(): string {
    if (!this.viewModel || this.viewModel.discussionStatus !== 'PUBLICATION_FAILED') {
      return '';
    }
    return this.viewModel.publicationFailureMessage || '';
  }

  get remainingTimeText(): string {
    if (!this.viewModel || !this.viewModel.discussionEndsAt) {
      return '';
    }
    const milliseconds = new Date(this.viewModel.discussionEndsAt).getTime() - this.nowProvider().getTime();
    if (milliseconds <= 0) {
      return '0d 0h';
    }
    const hours = Math.ceil(milliseconds / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    return days + 'd ' + (hours % 24) + 'h';
  }

  get hasFiles(): boolean {
    return !!this.primaryFile;
  }

  get primaryFileButtonText(): string {
    if (!this.visiblePrimaryFileName) {
      return '';
    }
    return this.truncateFileName(this.visiblePrimaryFileName);
  }

  get primaryFileButtonTitle(): string {
    const primaryFile = this.primaryFile;
    return primaryFile && primaryFile.displayName ? primaryFile.displayName : '';
  }

  get visiblePrimaryFileName(): string {
    return this.lastReplacedFileName || this.storedPrimaryFileName || this.primaryFileButtonTitle;
  }

  get primaryDownloadFileName(): string {
    return this.lastReplacedFileName || this.storedPrimaryFileName;
  }

  get fileReplacementDisplayName(): string {
    return this.selectedFileName || this.lastReplacedFileName;
  }

  get files(): StandardDiscussionFilesDto {
    return this.viewModel && this.viewModel.files ? this.viewModel.files : this.emptyFiles();
  }

  get primaryFile(): StandardDiscussionPrimaryFile | null {
    const files = this.files;
    if (files.ruDoc || files.ruFileLink) {
      return {
        type: 'RU_DOC',
        fileId: files.ruDoc,
        fileLink: files.ruFileLink,
        action: 'doc',
        displayName: this.fileNameFromLink(files.ruFileLink, '.docx')
      };
    }
    if (files.enDoc || files.enFileLink) {
      return {
        type: 'EN_DOC',
        fileId: files.enDoc,
        fileLink: files.enFileLink,
        action: 'doc',
        displayName: this.fileNameFromLink(files.enFileLink, '.docx')
      };
    }
    if (files.ruPdf) {
      return { type: 'RU_PDF', fileId: files.ruPdf, fileLink: null, action: 'pdf', displayName: this.standardTitleFileName('.pdf') };
    }
    if (files.enPdf) {
      return { type: 'EN_PDF', fileId: files.enPdf, fileLink: null, action: 'pdf', displayName: this.standardTitleFileName('.pdf') };
    }
    if (files.ruPpt) {
      return { type: 'RU_PPT', fileId: files.ruPpt, fileLink: null, action: 'download', displayName: this.standardTitleFileName('.ppt') };
    }
    if (files.enPpt) {
      return { type: 'EN_PPT', fileId: files.enPpt, fileLink: null, action: 'download', displayName: this.standardTitleFileName('.ppt') };
    }
    return null;
  }

  get canManageFiles(): boolean {
    const user = this.userService.getLocal();
    const roles = user && user.roles ? user.roles : [];
    return roles.indexOf('ROLE_ADMIN') !== -1 || roles.indexOf('ADMIN') !== -1;
  }

  ngOnInit(): void {
    this.subscriptions.push(this.route.params.subscribe((params) => this.loadRoute(params['id'])));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public onSendMessage(): void {
    if (!this.canSendMessage) {
      return;
    }
    this.postMessage(this.trimmedMessage());
  }

  public onMessageEnter(event: any): void {
    if (event && event.shiftKey) {
      return;
    }
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    this.onSendMessage();
  }

  public trackByGroupId(index: number, group: any): string | number {
    return group && group.groupId ? group.groupId : index;
  }

  public trackByMessageId(index: number, message: any): string | number {
    return message && message.messageId ? message.messageId : index;
  }

  public statusKey(status: string): string {
    return 'STANDARD_DISCUSSION_STATUS_' + status;
  }

  public isDocumentUpdatedMessage(message: any): boolean {
    var typedMessage = <{ type?: string }>message;
    return !!typedMessage && typedMessage.type === 'DOCUMENT_UPDATED';
  }

  public openPrimaryFile(): void {
    const primaryFile = this.primaryFile;
    if (!primaryFile) {
      return;
    }
    if (primaryFile.action === 'doc') {
      this.openDocFile(primaryFile.fileId, primaryFile.fileLink);
      return;
    }
    if (primaryFile.action === 'pdf') {
      this.openPdf(primaryFile.fileId);
      return;
    }
    this.downloadFile(primaryFile.fileId, this.primaryDownloadFileName);
  }

  public downloadPrimaryFile(): void {
    const primaryFile = this.primaryFile;
    if (!primaryFile) {
      return;
    }
    if (primaryFile.fileId) {
      this.downloadFile(primaryFile.fileId, this.primaryDownloadFileName);
      return;
    }
    if (primaryFile.fileLink) {
      this.downloadFileLink(primaryFile.fileLink, this.visiblePrimaryFileName);
    }
  }

  public goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  public senderInitials(senderName: string): string {
    const trimmedName = senderName ? senderName.trim() : '';
    if (!trimmedName) {
      return '?';
    }
    return trimmedName.split(/\s+/).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
  }

  public openDocFile(fileId: string, fileLink: string): void {
    if (fileLink) {
      localStorage.setItem('fileLink', fileLink);
      this.openDocModal(true);
      return;
    }
    if (!fileId || !this.activeStandardId) {
      return;
    }
    localStorage.setItem('DocxID', fileId);
    localStorage.setItem('DocxSID', this.activeStandardId);
    this.openDocModal(false);
  }

  public openPdf(fileId: string): void {
    if (!fileId) {
      return;
    }
    this.subscriptions.push(this.userService.getPdf(fileId).subscribe((response: any) => {
      const fileURL = window.URL.createObjectURL(new Blob([response.body], { type: 'application/pdf' }));
      window.open(fileURL, '_blank');
    }));
  }

  public downloadFile(fileId: string, fallbackFileName?: string): void {
    if (!fileId) {
      return;
    }
    this.subscriptions.push(this.userService.getFile(fileId).subscribe((response: any) => {
      const link = document.createElement('a');
      const objectUrl = window.URL.createObjectURL(response.body);
      link.href = objectUrl;
      link.download = this.downloadFileName(response.headers.get('filename'), fallbackFileName);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    }));
  }

  public downloadFileName(responseFileName: string, fallbackFileName?: string): string {
    if (fallbackFileName) {
      return fallbackFileName;
    }
    return this.cleanGeneratedFileName(responseFileName || '');
  }

  private downloadFileLink(fileLink: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = fileLink;
    if (fileName) {
      link.download = fileName;
    }
    link.click();
    link.remove();
  }

  public replaceDiscussionFile(event: any): void {
    if (!this.canManageFiles || !this.activeStandardId || this.isFileMutationInProgress) {
      return;
    }
    const file = event && event.target && event.target.files && event.target.files.length
      ? event.target.files[0]
      : null;
    if (!file) {
      this.selectedFileName = '';
      return;
    }
    this.selectedFileName = file.name;
    this.fileMutationSuccess = false;
    const formData = new FormData();
    formData.set('RU_DOC', file, file.name);
    this.isFileMutationInProgress = true;
    this.fileMutationError = false;
    this.subscriptions.push(this.adminService.uploadFiles({ id: this.activeStandardId }, formData).subscribe(
      () => {
        this.lastReplacedFileName = file.name;
        this.selectedFileName = '';
        this.saveFileReplacementMessage(file.name);
      },
      () => this.finishFileMutation(false)
    ));
  }

  private loadRoute(standardId: string): void {
    this.activeStandardId = standardId;
    this.viewState = 'loading';
    this.isMessagePosting = false;
    this.selectedFileName = '';
    this.lastReplacedFileName = '';
    this.storedPrimaryFileName = '';
    this.fileMutationSuccess = false;
    this.fileMutationError = false;
    this.loadDiscussion(standardId);
    this.loadMessages(standardId);
  }

  private loadDiscussion(standardId: string): void {
    this.subscriptions.push(this.discussionApi.getDiscussion(standardId).subscribe(
      (state) => this.applyDiscussionState(standardId, state),
      (error) => this.applyLoadError(standardId, error)
    ));
  }

  private loadMessages(standardId: string): void {
    this.subscriptions.push(this.discussionApi.getDiscussionMessages(standardId).subscribe(
      (response) => this.applyMessages(standardId, response),
      (error) => this.applyLoadError(standardId, error)
    ));
  }

  private postMessage(text: string): void {
    const standardId = this.activeStandardId;
    this.isMessagePosting = true;
    this.subscriptions.push(this.discussionApi.postDiscussionMessage(standardId, text).subscribe(
      (message) => this.applyPostedMessage(standardId, message),
      () => this.applyPostError(standardId)
    ));
  }

  private applyDiscussionState(standardId: string, state: StandardDiscussionStateDto): void {
    if (!this.isActiveStandard(standardId)) {
      return;
    }
    this.viewModel = this.mapDiscussionState(state);
    this.viewState = 'ready';
    this.loadStoredPrimaryFileName(standardId);
  }

  private applyMessages(standardId: string, response: StandardDiscussionMessagesDto): void {
    if (!this.isActiveStandard(standardId)) {
      return;
    }
    this.messages = response && response.messages ? response.messages : [];
  }

  private saveFileReplacementMessage(fileName: string): void {
    const standardId = this.activeStandardId;
    this.subscriptions.push(this.discussionApi.postDocumentUpdatedMessage(standardId, fileName).subscribe(
      (message) => {
        this.fileMutationSuccess = true;
        this.messages = this.messages.concat([message]);
        this.finishFileMutation(true);
      },
      () => this.finishFileMutation(false)
    ));
  }

  private loadStoredPrimaryFileName(standardId: string): void {
    const primaryFile = this.primaryFile;
    if (!this.shouldLoadStoredPrimaryFileName(primaryFile)) {
      return;
    }
    this.subscriptions.push(this.userService.getFile(primaryFile.fileId).subscribe(
      (response: any) => this.applyStoredPrimaryFileName(standardId, response),
      () => undefined
    ));
  }

  private shouldLoadStoredPrimaryFileName(primaryFile: StandardDiscussionPrimaryFile): boolean {
    if (!primaryFile || primaryFile.action !== 'doc' || !primaryFile.fileId) {
      return false;
    }
    if (!primaryFile.fileLink) {
      return true;
    }
    var extension = this.fileExtension(primaryFile.displayName) || '.docx';
    return primaryFile.displayName === this.standardTitleFileName(extension);
  }

  private applyStoredPrimaryFileName(standardId: string, response: any): void {
    if (!this.isActiveStandard(standardId) || this.lastReplacedFileName) {
      return;
    }
    var responseFileName = response && response.headers ? response.headers.get('filename') : '';
    var cleanFileName = this.cleanGeneratedFileName(responseFileName || '', '.docx');
    if (cleanFileName) {
      this.storedPrimaryFileName = cleanFileName;
    }
  }

  private applyPostedMessage(standardId: string, message: StandardDiscussionMessageDto): void {
    if (!this.isActiveStandard(standardId)) {
      return;
    }
    this.isMessagePosting = false;
    this.messages = this.messages.concat([message]);
    this.messageText = '';
    this.viewState = 'ready';
  }

  private applyPostError(standardId: string): void {
    if (!this.isActiveStandard(standardId)) {
      return;
    }
    this.isMessagePosting = false;
    this.viewState = 'message-error';
  }

  private applyLoadError(standardId: string, error: any): void {
    if (!this.isActiveStandard(standardId)) {
      return;
    }
    this.viewState = error && error.status === 403 ? 'access-denied' : 'load-error';
  }

  private mapDiscussionState(state: StandardDiscussionStateDto): StandardDiscussionStateDto {
    state.affectedGroups = state && state.affectedGroups ? state.affectedGroups : [];
    state.files = state && state.files ? state.files : this.emptyFiles();
    return state;
  }

  private finishFileMutation(success: boolean): void {
    this.isFileMutationInProgress = false;
    this.fileMutationError = !success;
    if (success && this.activeStandardId) {
      this.loadDiscussion(this.activeStandardId);
      this.loadMessages(this.activeStandardId);
    }
  }

  private trimmedMessage(): string {
    return this.messageText ? this.messageText.trim() : '';
  }

  private isActiveStandard(standardId: string): boolean {
    return standardId === this.activeStandardId;
  }

  private openDocModal(useManualLink: boolean): void {
    const modalRef = this.modalService.open(ViewDocxComponent, { size: 'lg', backdrop: 'static' });
    modalRef.result.then(
      () => this.clearDocModalStorage(useManualLink),
      () => this.clearDocModalStorage(useManualLink)
    );
  }

  private clearDocModalStorage(useManualLink: boolean): void {
    if (useManualLink) {
      this.viewDocxService.deleteLinkByDocxIDManually();
      return;
    }
    this.viewDocxService.clearTempFileLoacalStorage();
  }

  private emptyFiles(): StandardDiscussionFilesDto {
    return {
      ruDoc: null,
      ruFileLink: null,
      ruPdf: null,
      ruPpt: null,
      enDoc: null,
      enFileLink: null,
      enPdf: null,
      enPpt: null
    };
  }

  private fileNameFromLink(fileLink: string, fallbackExtension: string): string {
    if (!fileLink) {
      return this.standardTitleFileName(fallbackExtension);
    }
    var queryIndex = fileLink.indexOf('?');
    var cleanLink = queryIndex === -1 ? fileLink : fileLink.substring(0, queryIndex);
    var slashIndex = cleanLink.lastIndexOf('/');
    var fileName = slashIndex === -1 ? cleanLink : cleanLink.substring(slashIndex + 1);
    return this.cleanGeneratedFileName(fileName, fallbackExtension);
  }

  private cleanGeneratedFileName(fileName: string, fallbackExtension?: string): string {
    var decodedFileName = this.decodeFileName(fileName);
    var generatedNameMatch = decodedFileName.match(/^\d{4}-\d{2}-\d{2}T.+?_local_(.+)$/);
    if (generatedNameMatch && generatedNameMatch[1]) {
      decodedFileName = generatedNameMatch[1];
    }
    decodedFileName = decodedFileName.replace(/^tmp_\d+/, '');
    decodedFileName = decodedFileName.replace(/_/g, ' ').trim();
    return this.removeStandardNumberSuffix(decodedFileName, fallbackExtension || this.fileExtension(decodedFileName));
  }

  private removeStandardNumberSuffix(fileName: string, fallbackExtension: string): string {
    var standardNumber = this.viewModel && this.viewModel.standardNumber ? this.viewModel.standardNumber.trim() : '';
    if (!standardNumber) {
      return fileName;
    }
    var detectedExtension = this.fileExtension(fileName);
    var extension = detectedExtension || fallbackExtension || '';
    var baseName = detectedExtension ? fileName.substring(0, fileName.length - detectedExtension.length) : fileName;
    if (baseName === standardNumber) {
      return this.standardTitleFileName(extension);
    }
    return fileName;
  }

  private standardTitleFileName(extension: string): string {
    var title = this.viewModel && this.viewModel.standardTitle ? this.viewModel.standardTitle : '';
    title = title.replace(/[\\/:*?"<>|]+/g, '').trim();
    return title ? title + (extension || '') : '';
  }

  private fileExtension(fileName: string): string {
    var extensionMatch = fileName ? fileName.match(/\.[a-z0-9]+$/i) : null;
    return extensionMatch ? extensionMatch[0] : '';
  }

  private decodeFileName(fileName: string): string {
    try {
      return decodeURIComponent(fileName);
    } catch (error) {
      return fileName;
    }
  }

  private truncateFileName(fileName: string): string {
    var maxLength = 34;
    if (!fileName || fileName.length <= maxLength) {
      return fileName || '';
    }
    return fileName.substring(0, maxLength - 3) + '...';
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/standard-discussion/containers/standard-discussion-page/standard-discussion-page.component.ts